/**
 * Хранилища и сборщики: чем меряем и чем смотрим.
 *
 * Отдельным разделом, а не вкладкой общей картины: это состояние самой
 * консоли, а не хозяйства. Молчащий сборщик опаснее видимой ошибки — пока он
 * молчит, экран показывает вчерашнюю правду с сегодняшней уверенностью.
 */
import { useQuery } from '../transport/hooks.ts';
import { ago, agoPhrase, bytes } from '../lib/format.ts';
import type { OverviewSnapshot } from '../../shared/protocol/overview.ts';

export function Storage(): React.JSX.Element {
  const { data, error, loading } = useQuery<OverviewSnapshot>('overview.get');

  if (error) {
    return (
      <section className="panel panel--fill">
        <h2 className="panel__title">Хранилища и сборщики</h2>
        <p className="alert alert--error">Ошибка: {error.msg}</p>
      </section>
    );
  }
  if (!data) {
    return (
      <section className="panel panel--fill">
        <h2 className="panel__title">Хранилища и сборщики</h2>
        <p className="muted">{loading ? 'Спрашиваем…' : 'Нет данных.'}</p>
      </section>
    );
  }

  return (
    <section className="panel panel--fill">
      <header className="panel__head">
        <h2 className="panel__title">Хранилища и сборщики</h2>
      </header>

      <div className="panel__scroll">
        <div className="strip">
          <div className="strip__block">
            <h3 className="strip__title">Хранилища</h3>
            <ul className="strip__list">
              {data.storage.map((s) => {
                const used = s.totalBytes > 0 ? 1 - s.freeBytes / s.totalBytes : 0;
                return (
                  <li key={s.key}>
                    <span className="strip__name">{s.label}</span>
                    {/*
                      «Не измерено» и «места нет» — разные вещи, и показывать
                      их одинаково нельзя: нули читаются как заполненный том.
                    */}
                    {s.unavailable ? (
                      <span className="strip__unknown">не измерено — {s.unavailable}</span>
                    ) : (
                      <>
                        <span className="strip__value">
                          {bytes(s.freeBytes)} свободно из {bytes(s.totalBytes)}
                          {' · занято '}{Math.round(used * 100)}%
                        </span>
                        <span className="bar" aria-hidden="true">
                          <span
                            className={`bar__fill${used > 0.9 ? ' bar__fill--hot' : used > 0.75 ? ' bar__fill--warm' : ''}`}
                            style={{ width: `${Math.min(100, Math.round(used * 100))}%` }}
                          />
                        </span>
                      </>
                    )}
                    {s.sameVolumeAs && (
                      <span className="strip__note">
                        тот же том, что «{s.sameVolumeAs}» — место общее
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="strip__block">
            <h3 className="strip__title">Сборщики</h3>
            <ul className="strip__list">
              {data.collectors.map((c) => (
                <li key={c.name}>
                  <span className="strip__name">{c.label}</span>
                  <span className={c.known === false ? 'strip__unknown' : 'strip__value'}>
                    {c.known === false ? 'состояние неизвестно — worker молчит'
                      : !c.enabled ? 'выключен'
                      : c.lastRunAt === null ? 'молчит'
                      : c.ok ? agoPhrase(c.lastRunAt)
                      : 'сбой'}
                  </span>
                  {c.error && <span className="strip__note">{c.error}</span>}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
